/* ═══════════════════════════════════════════════════════════════
   formaCollab — the shared forma document (Sync appendix item 6).

   PURE: Yjs in, Yjs out. No React, no I/O, no sockets.

   Shape: ONE root Y.Map ('forma') mirroring StandardConfig — plain objects
   become Y.Map, arrays become Y.Array, everything else (strings, numbers,
   booleans, null, image base64) is a leaf. There is no second schema: a new
   optional key on StandardConfig (tokenStyles, eosStyle …) just works, the
   diff and the reader are generic.

   A 'meta' map beside it carries the Designer's flags (seeded, seeder,
   edited) plus `dirty` (unpublished edits exist — true on any edit
   transaction, false on Publish). It is deliberately OUTSIDE the root map so
   an UndoManager scoped to the forma never undoes a flag.
   ═══════════════════════════════════════════════════════════════ */
import * as Y from 'yjs';
import type { StandardConfig } from '../../shared/types';

export const FORMA_ROOT = 'forma';
export const FORMA_META = 'meta';

type Json = unknown;

const isObj = (v: Json): v is Record<string, Json> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

/** Plain JSON → the Yjs tree (objects → Y.Map, arrays → Y.Array, rest leaf). */
function toY(v: Json): unknown {
  if (Array.isArray(v)) {
    const a = new Y.Array<unknown>();
    a.push(v.filter((x) => x !== undefined).map(toY));
    return a;
  }
  if (isObj(v)) {
    const m = new Y.Map<unknown>();
    for (const [k, x] of Object.entries(v)) if (x !== undefined) m.set(k, toY(x));
    return m;
  }
  return v;
}

/** The Yjs tree → plain JSON. */
function fromY(v: unknown): Json {
  if (v instanceof Y.Map) {
    const out: Record<string, Json> = {};
    v.forEach((x, k) => {
      out[k] = fromY(x);
    });
    return out;
  }
  if (v instanceof Y.Array) return v.toArray().map(fromY);
  return v;
}

function deepEqual(a: Json, b: Json): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => deepEqual(x, b[i]));
  }
  if (isObj(a) && isObj(b)) {
    const ka = Object.keys(a).filter((k) => a[k] !== undefined);
    const kb = Object.keys(b).filter((k) => b[k] !== undefined);
    return ka.length === kb.length && ka.every((k) => k in b && deepEqual(a[k], b[k]));
  }
  return false;
}

/** Reconcile one Y.Map slot with a new value, touching only what changed. */
function diffMapKey(map: Y.Map<unknown>, key: string, before: Json, after: Json): void {
  if (after === undefined) {
    if (map.has(key)) map.delete(key);
    return;
  }
  if (!map.has(key)) {
    map.set(key, toY(after));
    return;
  }
  // The local edit changed nothing here: leave the live value alone, so a
  // colleague's concurrent edit of this path is never clobbered by a stale copy.
  if (deepEqual(before, after)) return;
  const cur = map.get(key);
  if (cur instanceof Y.Map && isObj(after)) diffMap(cur, isObj(before) ? before : {}, after);
  else if (cur instanceof Y.Array && Array.isArray(after)) {
    diffArray(cur, Array.isArray(before) ? before : [], after);
  } else map.set(key, toY(after)); // leaf change, or a leaf <-> container swap
}

function diffMap(map: Y.Map<unknown>, before: Record<string, Json>, after: Record<string, Json>) {
  for (const k of Array.from(map.keys())) {
    // Delete only what THIS edit removed (present before, absent after).
    if (before[k] !== undefined && (!(k in after) || after[k] === undefined)) map.delete(k);
  }
  for (const k of Object.keys(after)) diffMapKey(map, k, before[k], after[k]);
}

/** Arrays diff per index: replace in place, push the tail, truncate. Length
 *  changes are judged against `before`, never the live length, so a colleague's
 *  concurrent push is not truncated by an edit elsewhere in the same array. */
function diffArray(arr: Y.Array<unknown>, before: Json[], after: Json[]): void {
  const next = after.filter((x) => x !== undefined);
  const common = Math.min(arr.length, before.length, next.length);
  for (let i = 0; i < common; i++) {
    const b = before[i];
    if (deepEqual(b, next[i])) continue; // untouched locally
    const cur = arr.get(i);
    if (deepEqual(fromY(cur), next[i])) continue; // already that value
    if (cur instanceof Y.Map && isObj(next[i]) && isObj(b)) {
      diffMap(cur, b, next[i] as Record<string, Json>);
    } else if (cur instanceof Y.Array && Array.isArray(next[i]) && Array.isArray(b)) {
      diffArray(cur, b, next[i] as Json[]);
    } else {
      arr.delete(i, 1);
      arr.insert(i, [toY(next[i])]);
    }
  }
  if (next.length < before.length) {
    const from = Math.min(next.length, arr.length);
    if (arr.length > from) arr.delete(from, Math.min(before.length, arr.length) - from);
  } else if (next.length > before.length) {
    arr.push(next.slice(before.length).map(toY));
  }
}

export function formaMeta(ydoc: Y.Doc): Y.Map<unknown> {
  return ydoc.getMap(FORMA_META);
}

function formaRoot(ydoc: Y.Doc): Y.Map<unknown> {
  return ydoc.getMap(FORMA_ROOT);
}

/** Has any writer seeded this document yet? */
export function isFormaSeeded(ydoc: Y.Doc): boolean {
  return formaMeta(ydoc).get('seeded') === true;
}

/** First writer only: write the whole forma into an empty document and flag it
 *  seeded. No-op (returns false) when the document is already seeded. */
export function seedForma(
  ydoc: Y.Doc,
  forma: StandardConfig,
  origin?: unknown,
  seeder?: string
): boolean {
  if (isFormaSeeded(ydoc)) return false;
  ydoc.transact(() => {
    const root = formaRoot(ydoc);
    for (const [k, v] of Object.entries(forma)) if (v !== undefined) root.set(k, toY(v));
    const meta = formaMeta(ydoc);
    meta.set('seeded', true);
    if (seeder) meta.set('seeder', seeder);
  }, origin);
  return true;
}

/** Structural diff: write ONLY the changed leaves, in ONE transaction tagged
 *  `origin`. Flags the document edited + dirty in the same transaction. A diff
 *  with no change opens no transaction. */
export function applyFormaDiff(
  ydoc: Y.Doc,
  before: StandardConfig,
  after: StandardConfig,
  origin: unknown
): void {
  if (deepEqual(before, after)) return;
  ydoc.transact(() => {
    diffMap(
      formaRoot(ydoc),
      before as unknown as Record<string, Json>,
      after as unknown as Record<string, Json>
    );
    const meta = formaMeta(ydoc);
    if (meta.get('edited') !== true) meta.set('edited', true);
    if (meta.get('dirty') !== true) meta.set('dirty', true);
  }, origin);
}

/** The reverse of applyFormaDiff/seedForma: the document as a StandardConfig. */
export function readForma(ydoc: Y.Doc): StandardConfig {
  return fromY(formaRoot(ydoc)) as StandardConfig;
}

/** Set the unpublished-edits flag (false on Publish). No-op when unchanged. */
export function markDirty(ydoc: Y.Doc, dirty: boolean, origin?: unknown): void {
  const meta = formaMeta(ydoc);
  if (meta.get('dirty') === dirty) return;
  ydoc.transact(() => {
    meta.set('dirty', dirty);
    if (dirty && meta.get('edited') !== true) meta.set('edited', true);
  }, origin);
}

export function isFormaDirty(ydoc: Y.Doc): boolean {
  return formaMeta(ydoc).get('dirty') === true;
}

/** Ctrl+Z for THIS client only: scoped to the forma root, tracking only
 *  `origin`. captureTimeout 0 = one applyFormaDiff transaction is one step
 *  (the Studio's own 100-step depth is kept by useUndoCoordinator). */
export function createFormaUndoManager(ydoc: Y.Doc, origin: unknown): Y.UndoManager {
  return new Y.UndoManager(formaRoot(ydoc), {
    trackedOrigins: new Set([origin]),
    captureTimeout: 0,
  });
}
